import i18next from 'i18next';

/**
 * Translates a dynamic value if a translation exists for it
 * @param value The value to translate
 * @param namespace The namespace to look for translations in (default: 'uom')
 * @param fallback The fallback value if no translation is found (default: the original value)
 * @returns The translated value or the fallback
 */
export const translateDynamicValue = (
	value: string,
	namespace: string,
	fallback?: string
): string => {
	// Check if the translation exists
	const translationKey = `${namespace}.${value}`;
	const translation = i18next.t(translationKey);
  
	// If the translation is the same as the key, it means no translation was found
	if (typeof translation === 'string' && translation === translationKey) {
		return fallback || value;
	}
  
	return typeof translation === 'string' ? translation : (fallback || value);
};

/**
 * Translates a unit of measure (UOM)
 * @param uom The unit of measure to translate
 * @param fallback The fallback value if no translation is found
 * @returns The translated UOM or the fallback
 */
export const translateUOM = (uom: string, fallback?: string): string => {
	return translateDynamicValue(uom, 'uom', fallback);
};

/**
 * Translates an additional fee name
 * @param fee The fee name to translate
 * @param fallback The fallback value if no translation is found
 * @returns The translated fee or the fallback
 */
export const translateAdditionalFee = (fee: string, fallback?: string): string => {
	return translateDynamicValue(fee, 'additionalFees', fallback);
};

/**
 * Translates a resource type
 * @param resourceType The resource type to translate
 * @param fallback The fallback value if no translation is found
 * @returns The translated resource type or the fallback
 */
export const translateResourceType = (resourceType: string, fallback?: string): string => {
	return translateDynamicValue(resourceType, 'resourceTypes', fallback);
}; 

/**
 * Translates a tool name
 * @param toolName The tool name to translate
 * @param fallback The fallback value if no translation is found
 * @returns The translated tool name or the fallback
 */
export const translateToolName = (toolName: string, fallback?: string): string => {
	return translateDynamicValue(toolName, 'tools', fallback);
};

export const translateSalesOrderStatus = (status: string, fallback?: string): string => {
	return translateDynamicValue(status, 'salesOrderStatus', fallback);
};